import {
  ClipboardCheck,
  Edit2,
  MapPin,
  Mic2,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'

import { Button } from '@/components/ui/button'

type TableItemProps = {
  id: string
  tableNumber: string
  capacity?: number
  occupancy?: number
  notes?: string
  type?: 'table' | 'stage' | 'registration' | 'other'
  onEdit: () => void
  onDelete: () => void
  onAddGuest?: () => void
}

export const TableItem = ({
  id,
  tableNumber,
  capacity = 10,
  occupancy = 0,
  notes,
  type = 'table',
  onEdit,
  onDelete,
  onAddGuest,
}: TableItemProps) => {
  const isTable = type === 'table'

  const getTypeConfig = () => {
    switch (type) {
      case 'stage':
        return {
          icon: <Mic2 className="size-5" />,
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-500',
          label: tableNumber || 'Stage',
        }
      case 'registration':
        return {
          icon: <ClipboardCheck className="size-5" />,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-500',
          label: tableNumber || 'Registration',
        }
      case 'other':
        return {
          icon: <MapPin className="size-5" />,
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-500',
          label: tableNumber || 'Other',
        }
      default:
        return {
          icon: null,
          bgColor: 'bg-primary/5',
          textColor: 'text-primary',
          label: tableNumber.startsWith('โต๊ะ')
            ? tableNumber
            : `โต๊ะ ${tableNumber}`,
        }
    }
  }

  const config = getTypeConfig()

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="group hover:border-primary/20 relative flex flex-col gap-3 rounded-[24px] border border-gray-100 bg-white p-4 shadow-[0_4px_20px_rgb(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-1 items-center gap-3">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-base font-bold shadow-xs ring-1 ring-black/3 ${config.bgColor} ${config.textColor}`}
            >
              {isTable ? tableNumber : config.icon}
            </div>
            <div className="flex min-w-0 flex-col">
              <h3 className="truncate text-[15px] leading-tight font-bold tracking-tight text-gray-900">
                {config.label}
              </h3>
              {isTable && (
                <div
                  className={`mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold ${
                    occupancy >= capacity
                      ? 'text-destructive animate-pulse'
                      : 'text-muted-foreground opacity-70'
                  }`}
                >
                  <Users className="size-3" />
                  {occupancy} / {capacity} ที่นั่ง
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            {isTable && (
              <Button
                size="icon"
                variant="ghost"
                className="hover:bg-primary/5 hover:text-primary size-9 rounded-full text-gray-400 transition-colors"
                onClick={onAddGuest}
              >
                <UserPlus className="size-4" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-primary/5 hover:text-primary size-9 rounded-full text-gray-400 transition-colors"
              onClick={onEdit}
            >
              <Edit2 className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-destructive/5 hover:text-destructive text-muted-foreground size-9 rounded-full transition-colors"
              onClick={onDelete}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>

        {notes && (
          <div className="border-border/40 rounded-xl border bg-gray-50/50 px-3 py-2">
            <p className="text-muted-foreground line-clamp-1 text-[11px] font-medium italic opacity-80">
              {notes}
            </p>
          </div>
        )}
      </m.div>
    </LazyMotion>
  )
}
